<template>
  <div>
    <div class="bg-img--wrapper" :class="{'is-lobby': isBlurBackground}" v-if="slideActive.type">
      <div
        v-if="canViewThemeGallery"
        class="w-full h-full grid"
      >
        <div
          class="bg-base-color col-start-1 row-start-1 w-full h-full"
          :style="{ backgroundColor: currentPresentationTheme.baseColour }"
        ></div>
        <div
          v-if="currentPresentationTheme?.backgroundImage"
          class="bg-image col-start-1 row-start-1 bg-cover w-full h-full bg-no-repeat bg-center"
          :style="{
            backgroundImage: `url(${currentPresentationTheme.backgroundImage?.large})`,
            opacity: currentPresentationTheme.visibility / 100 }"
        ></div>
      </div>
      <div
        class="slide-full-size slide-base-colour"
        :class="{'no-opacity': documentImageVisible}"
        :style="{ 'background-color': transitionBaseColour }"
      />
      <div
        class="transition-start-bg slide-image-bg"
        :class="{'slide-type-image-bg': documentImageVisible}"
        :style="{ 'background-image': 'url(' + transitionOldBackgroundImage + ')',
                  'background-size': `${slideActive.backgroundSize || 'cover'}`,
                  'opacity': 1 - slideVisibility }"
      >
        <div
          class="transition-end-bg"
          :class="{'animate__animated animate__fadeIn':showingAnimationClass}"
        >
          <div
            class="slide-image-bg"
            :class="{'slide-type-image-bg': documentImageVisible}"
            :style="{
              'background-image': 'url(' + transitionBackgroundImage + ')',
              'background-color': transitionBaseColour,
              'background-size': `${slideActive.backgroundSize || 'cover'}`,
              'opacity': 1 - slideVisibility
            }"
          />
        </div>
      </div>
    </div>

    <!-- Image slide -->
    <div
      class="slide-type-image-bg"
      :class="{
        'slide-type-img-fs': imageSlideBackgroundVisible,
        'transition-image-slide-bg': showingAnimationImgSlideBackground
      }"
      v-if="imageSlideBackgroundVisible"
      id="slide-type-image-bg"
      :style="{ 'background-image': 'url(' + transitionSlideImageFullScreen + ')' }"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import EventBus from '@/eventBus'
import constant from '@/constant'

export default {
  data() {
    return {
      firstTimeLoaded: true,
      transitionBackgroundImage: '',
      transitionBaseColour: '',
      transitionOldBackgroundImage: '',
      transitionSlideImageFullScreen: '',
      showingAnimationClass: false,
      showingAnimationImgSlideBackground: false,
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isLobbyScreen']),
    ...mapGetters('slideModule', ['slideActive', 'canUseXXLImage', 'isLegacyBackgroundImageThemeUrl', 'isLegacySlideDocumentImageUrl']),
    ...mapGetters('themeModule', ['canViewThemeGallery', 'currentPresentationTheme']),
    ...mapState({
      activeSlideId: (state) => state.activeSlideId,
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      sharePresentation: (state) => state.sharePresentation,
    }),
    slideImage() {
      const xxLarge = this.slideActive?.image?.xxLarge
      const xLarge = this.slideActive?.image?.xLarge

      if (this.canUseXXLImage && !this.isLegacySlideDocumentImageUrl && !this.imageSlideBackgroundVisible) {
        return xxLarge || xLarge
      }
      return xLarge
    },
    slideBackgroundImage() {
      const xxLarge = this.slideActive?.backgroundImage?.xxLarge
      const xLarge = this.slideActive?.backgroundImage?.xLarge
      // we need to check the isLegacyBackgroundImageThemeUrl because the legacy theme background doesn't have the xLarge size in file name (with the 2560x1440- prefix)
      // even if it has extreme high res but if we don't have that file, we can't use it
      if (this.canUseXXLImage && !this.isLegacyBackgroundImageThemeUrl) {
        // We need fallback because the xxl is our additional image size, the returned value from the upload modal doesn't have it
        // So we need to fallback to the xLarge image size
        return xxLarge || xLarge
      }
      return xLarge
    },
    defaultSlideBaseColour() {
      return this.canViewThemeGallery ? null : '#ffffff'
    },
    documentImageVisible() {
      return this.slideActive.image && (this.slideActive.type === 'document' || this.slideActive.type === 'freestyle')
    },
    imageSlideBackgroundVisible() {
      const { type, image, imageType } = this.slideActive
      return type === 'image' && image && imageType === 'background'
    },
    backgroundAndDocumentImageVisible() {
      const { backgroundImage, image, type } = this.slideActive
      return backgroundImage || (image && type === 'document')
    },
    slideVisibility() {
      const { backgroundImage, visibility } = this.slideActive
      if (backgroundImage) {
        if (visibility) {
          if (visibility == 0) {
            return 0.999
          }
          return 1 - (visibility / 100)
        }
        if (visibility == 0) {
          return 0.999
        }
        return null
      }
      return null
    },
    isBlurBackground() {
      return this.isLobbyScreen && this.isPresenting && this.slideActive.type === constant.SLIDE_TYPE.PICK_ANSWER
    },
  },
  watch: {
    activeSlideId() {
      if (this.firstTimeLoaded) {
        this._setValueForTransition()
        this.firstTimeLoaded = false
      }
    },
  },
  mounted() {
    EventBus.$on('eventHandleSetValueTransition', () => {
      this._setValueNoAnimationForChangeSlide()
    })
    EventBus.$on('setValueForTransition', () => {
      this._setValueForTransition()
    })
    EventBus.$on('updateValueWhenTransitionEnds', () => {
      this._updateValueWhenTransitionEnds()
    })
    EventBus.$on('checkShowingAnimationImgSlideBackground', () => {
      this._checkShowingAnimationImgSlideBackground()
    })
    EventBus.$on('changeShowingAnimationClass', (value) => {
      this.showingAnimationClass = value
    })
  },
  destroyed() {
    EventBus.$off('eventHandleSetValueTransition')
    EventBus.$off('setValueForTransition')
    EventBus.$off('updateValueWhenTransitionEnds')
    EventBus.$off('checkShowingAnimationImgSlideBackground')
    EventBus.$off('changeShowingAnimationClass')
  },
  methods: {
    async _setValueNoAnimationForChangeSlide() {
      const {
        backgroundImage, baseColour,
      } = this.slideActive

      if (this.documentImageVisible) {
        this._setTransitionImage(this.slideImage, 'old')
      } else if (backgroundImage) {
        this._setTransitionImage(this.slideBackgroundImage, 'old')
      } else if (!backgroundImage || !this.documentImageVisible) {
        this._setTransitionImage(null, 'old')
      }
      if (this.imageSlideBackgroundVisible) {
        this.transitionSlideImageFullScreen = this.slideImage
      } else {
        this.transitionSlideImageFullScreen = null
      }
      this.transitionBaseColour = baseColour || this.defaultSlideBaseColour
    },

    _setTransitionImage(image, old) {
      this.transitionBackgroundImage = image
      if (old) {
        this.transitionOldBackgroundImage = image
      }
    },

    _setValueForTransition() {
      const {
        baseColour, image, type, backgroundImage,
      } = this.slideActive
      this.transitionBaseColour = baseColour || this.defaultSlideBaseColour
      if (backgroundImage) {
        if (type === 'document' && image && this.slideImage) {
          this._setTransitionImage(this.slideImage)
        } else {
          this._setTransitionImage(this.slideBackgroundImage)
        }
      } else if (this.documentImageVisible) {
        this._setTransitionImage(this.slideImage)
      } else if (!backgroundImage || !this.documentImageVisible) {
        this._setTransitionImage(null)
      }
      if (this.imageSlideBackgroundVisible) {
        this.transitionSlideImageFullScreen = this.slideImage
      }
    },

    _updateValueWhenTransitionEnds() {
      setTimeout(() => {
        const { backgroundImage, image } = this.slideActive
        this.showingAnimationClass = false
        if (backgroundImage) {
          this.transitionOldBackgroundImage = backgroundImage.large
        } else if (this.documentImageVisible) {
          this.transitionOldBackgroundImage = image.large
        } else if (!backgroundImage || !this.documentImageVisible) {
          this.transitionOldBackgroundImage = null
        }
      }, 500)
    },

    _checkShowingAnimationImgSlideBackground() {
      if (this.imageSlideBackgroundVisible && this.isPresenting) {
        this.showingAnimationImgSlideBackground = true
      } else if (this.imageSlideBackgroundVisible && !this.isPresenting) {
        this.showingAnimationImgSlideBackground = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.bg-img--wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  &.is-lobby {
    &:before {
      z-index: 1;
      content: '';
      width: 100%;
      height: 100%;
      position: absolute;
      backdrop-filter: blur(20px);
    }
  }
}
.transition-end-bg {
  width: 100%;
  height: 100%;
  &.animate__animated {
    animation-duration: 0.5s;
    animation-timing-function: cubic-bezier(0.25, 1, 0.5, 1);
  }
}
.transition-start-bg {
  width: 100%;
  height: 100%;
}
.slide-base-colour {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.slide-type-image-bg, .slide-image-bg {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  &.gif-image {
    background-size: cover;
  }
}

.slide-type-img-fs {
  background-size: contain;
}

.slide-full-size {
  width: 100%;
  height: 100%;

  &.no-opacity {
    opacity: 1 !important;
  }
}
</style>
